import React, { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ContentEditableModule from 'react-contenteditable'
import DOMPurify from 'dompurify'

import Box from '@mui/material/Box'

import {
  setStikiContent,
  setStikiDirty,
  updateStikiAsync,
  selectStiki,
} from './stikisSlice'

import {
  selectStikisMove,
  selectStikisResize,
  selectIsResizing,
  selectMarqueeIsSelecting,
} from '../ui/uiSlice'

import { selectPageIsMine } from '../pages/pagesSlice'

const classes = {
  contentContainer: {
    // .contentContainer {
    position: 'absolute',
    left: '1em',
    top: '1em',
    right: '1em',
    bottom: '0.1em',
    '& .stikiContent': {
      // .stikiContent {
      fontSize: '100%',
      position: 'relative',
      width: '100%',
      height: '100%',
      wordSpacing: '0.5px',
      overflow: 'hidden',
    },
    '& .stikiContentSelected': {
      // .stiki.selected .stikiContent {
      cursor: 'text',
      overflowX: 'hidden',
      overflowY: 'auto',
      border: '0px solid black',
      '&:active, &:focus': {
        // .stiki.selected .stikiContent:active,
        // .stiki.selected .stikiContent:focus {
        outline: 'none',
        border: 'none', // 0.05em solid #9ecaed,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        boxShadow: '0 0 0.4em 0.15em rgb(163, 210, 255)',
      },
      '& a': {
        // .stikiContent a {
        color: '#295c96',
      },
    },
    '& .stikiContentCurrent': {},
  },
  contentContainerSelected: {
    // .stiki.selected .contentContainer {
    bottom: '1em',
  },
}

const ContentEditable =
  ContentEditableModule.default || ContentEditableModule

export default function StikiContent({ stikiId, isSelected, isCurrent }) {
  const stiki = useSelector(selectStiki(stikiId))
  const isMine = useSelector(selectPageIsMine(stiki?.page_id))
  const stikisMove = useSelector(selectStikisMove)
  const stikisResize = useSelector(selectStikisResize)

  const isSelecting = useSelector(selectMarqueeIsSelecting)
  const isResizing = useSelector(selectIsResizing)

  ///////////////////////////////////////////////
  //
  // Hooks
  //

  const dispatch = useDispatch()
  const contentEditableText = useRef(stiki.text)

  ///////////////////////////////////////////////
  //
  // ContentEditable event handlers
  //

  const handleMouseDown = (e) => {
    // console.log('<Stiki/>.handleContentMouseDown()')
    if (stiki.isSelected) {
      e.stopPropagation()
    }
  }

  const handleMouseMove = (e) => {
    // console.log('<Stiki/>.handleContentMouseMove()')
    // While a selection box is being dragged (`isSelecting`), the move has
    // to reach it, or the box stops growing whenever the pointer is over a stiki.
    if (
      !stikisMove.isMoving &&
      !isSelecting &&
      !stikisResize.isResizing
    ) {
      e.stopPropagation()
    }
  }

  const handleMouseUp = (e) => {
    // console.log('<Stiki/>.handleContentMouseUp()')
    // Likewise the release, or letting go over a stiki never ends the selection.
    if (
      !stikisMove.isMoving
      && !isSelecting
    ) {
      e.stopPropagation()
    }
  }

  const handleClick = (e) => {
    e.stopPropagation()
  }

  const handleContentChange = (e) => {
    contentEditableText.current = e.target.value
    // console.log('<Stiki/>.handleContentChange()\n', contentEditableText.current)
    if (!stiki.isDirty) {
      dispatch(setStikiDirty(stiki.id))
    }
    // e.stopPropagation(e)
  }

  const handleContentBlur = (e) => {
    // console.log('<Stiki/>.handleContentBlur()')
    const sanitizedHtml = DOMPurify.sanitize(contentEditableText.current, sanitizeConf)
    contentEditableText.current = sanitizedHtml
    if (stiki.text !== sanitizedHtml) {
      dispatch(setStikiContent({ stiki_id: stiki.id, text: sanitizedHtml }))
      dispatch(updateStikiAsync(stiki.id))
    }
  }

  const handleKeyDown = (e) => {
    // console.log('<StikiContent/>.handleContentKeyDown()')
    e.stopPropagation()
  }

  const handleWheel = (e) => {
    console.log('<StikiContent />.handleWheel()')
    if (isSelected && isCurrent) {
      e.stopPropagation()
    }
  }

  ///////////////////////////////////////////////
  //
  // Layout constants
  //

  const sanitizeConf = {
    ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'p', 'h1', 'br', 'div'],
    ALLOWED_ATTR: ['href'],
  }

  return (
    <Box
      sx={{
        ...classes.contentContainer,
        ...(isSelected ? classes.contentContainerSelected : {}),
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
    >
      <ContentEditable
        className={`stikiContent ${isSelected ? 'stikiContentSelected' : ''} ${
          isCurrent ? 'stikiContentCurrent' : ''
        } `}
        html={contentEditableText.current}
        // Not editable on a page someone shared with me. `disabled` sets
        // contentEditable=false, so the text can still be selected and copied,
        // which a padlock over the whole canvas would have taken away.
        disabled={!isMine}
        onChange={handleContentChange}
        onBlur={handleContentBlur}
      />
    </Box>
  )
}
